import { useApolloClient } from "@apollo/client";
import { Text } from "@mantine/core";
import {
  GetAgreementPageDocument,
  GetAgreementPageQuery,
  GetAgreementPageQueryVariables,
  useUpdateAgreementMutation,
} from "src/generated/graphql";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback, useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
  ROUTES,
  ROUTE_NAMES,
} from "src/utils/constants";
import { Button } from "@mantine/core";
import { useModals } from "@mantine/modals";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import AgreementForm from "../Detail/AgreementForm";
import { useDisclosure } from "@mantine/hooks";
import useWritePermission from "src/hooks/usePermission";
import useCustomModal from "src/hooks/useCustomModal";

export type AgreementData = ArrayElement<
  GetAgreementPageQuery["agreementPage"]["items"]
>;

type Props = {
  isOrg?: boolean;
};

const AgreementList = ({ isOrg }: Props) => {
  const modals = useModals();
  const isAllowWrite = useWritePermission(
    isOrg ? EMPLOYEE_ROLE_OBJ.orgAgreements : EMPLOYEE_ROLE_OBJ.locAgreements
  );
  const navigate = useNavigate();
  const { setCurrentPageTitle, currentLocation } = useContext(AppContext);
  const [watchRefetch, { toggle: refetch }] = useDisclosure(false);
  const { query } = useApolloClient();
  const { ShowActiveStatus, GridOptions } = useCustomModal();

  const getAgreements = useCallback(
    async (fetchParams: FetchParams): Promise<QueryResponse<AgreementData>> => {
      let { data } = await query<
        GetAgreementPageQuery,
        GetAgreementPageQueryVariables
      >({
        query: GetAgreementPageDocument,
        variables: {
          pagination: {
            count: fetchParams.count,
            page: fetchParams.page,
            orderBy: fetchParams.orderBy || DEFAULT_ORDER.agreement_name,
            filters: fetchParams.filters,
          },
          locationIds: isOrg
            ? undefined
            : currentLocation
            ? [{ id: currentLocation.id }]
            : [],
        },
      });

      if (data.agreementPage) {
        return {
          rowCount: data.agreementPage.totalRows,
          rowData: data.agreementPage.items as AgreementData[],
        };
      }
      return {
        rowData: [],
        rowCount: 0,
      };
    },
    [isOrg, currentLocation]
  );

  useEffect(() => {
    refetch();
  }, [isOrg, currentLocation]);

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.AGREEMENT);

    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleNavigation = (id: string): void => {
    isOrg
      ? navigate(`${ROUTES.ORG_AGREEMENT}/${id}`)
      : navigate(`${ROUTES.AGREEMENT}/${id}`);
  };

  const handleModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Agreement",
      children: (
        <AgreementForm
          onCreate={() => {
            refetch();
            modals.closeAll();
          }}
          isOrg={isOrg}
          isAllowWrite={isAllowWrite}
        />
      ),
    });
  }, [isOrg, isAllowWrite]);

  const [updateAgreement] = useUpdateAgreementMutation({
    onCompleted: () => {
      refetch();
    },
  });

  const onUpdate = (id: string, isActive: boolean) => {
    updateAgreement({ variables: { id, data: { isActive } } });
  };

  const TableGrid = useCallback(
    () => (
      <ScrollableDataGrid<AgreementData>
        topMargin={56}
        columnConfigs={[
          {
            field: "agreement_name",
            name: "Name",
            filter: {
              field: "name",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem.id)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.name}
              </Text>
            ),
          },
          {
            field: "agreement_version",
            name: "Version",
            filter: {
              field: "version",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {rowItem.version}
              </Text>
            ),
          },
          {
            field: "isMembership",
            name: "Membership ",
            filter: {
              field: "isMembership",
              type: "select",
              options: ["true", "false"],
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {rowItem.isMembership ? "true" : "false"}
              </Text>
            ),
          },
          {
            field: "termDetail",
            name: "Term",
            filter: {
              field: "termDetail",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {rowItem.termValue} {rowItem.Unit}
              </Text>
            ),
          },
          {
            field: "isAutoRenew",
            name: "Auto-Renew",
            filter: {
              field: "isAutoRenew",
              type: "select",
              options: ["true", "false"],
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {rowItem.isAutoRenew ? "true" : "false"}
              </Text>
            ),
          },
          {
            field: "isOrg",
            name: "Org-Level",
            filter: {
              field: "isOrg",
              type: "select",
              options: ["true", "false"],
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text weight={500} style={{ cursor: "pointer" }}>
                {rowItem.isOrg ? "true" : "false"}
              </Text>
            ),
          },
          {
            field: "isActive",
            name: "Active",
            sortable: true,
            cellRenderer: ({ isActive }) => ShowActiveStatus(isActive),
          },
          ...(isAllowWrite
            ? [
                {
                  field: "",
                  name: "",
                  cellRenderer: ({ id, isActive }: AgreementData) => (
                    <GridOptions
                      id={id}
                      onUpdate={onUpdate}
                      isActive={isActive}
                    />
                  ),
                },
              ]
            : []),
        ]}
        dataFetch={getAgreements}
      />
    ),
    [watchRefetch, isAllowWrite]
  );

  return (
    <div>
      {isAllowWrite && (
        <Button style={{ marginBottom: 20 }} onClick={handleModal}>
          Create +
        </Button>
      )}
      <TableGrid />
    </div>
  );
};

export default AgreementList;
